import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import useLazyCursorPagination from "../../hooks/useLazyCursorPagination";
import LeadService from "../../services/LeadService";
import { cn } from "../../utils/styles.utils";
import { LeadStatus, LeadStatusColors } from "../../constants/leadStatus";

const SORTABLE_COLUMNS = [
  { key: "name", label: "Name", minWidth: 220 },
  { key: "email", label: "Email", minWidth: 250 },
  { key: "status", label: "Status", minWidth: 150 },
  { key: "source", label: "Source", minWidth: 150 },
  { key: "company", label: "Company", minWidth: 220 },
  { key: "phoneNumber", label: "Phone Number", minWidth: 170 },
  { key: "notes", label: "Notes", minWidth: 220 },
  { key: "businessDetails", label: "Business Details", minWidth: 230 },
  { key: "followupDate", label: "Follow-up Date", minWidth: 200 },
  { key: "contactOn", label: "Contact On", minWidth: 200 },
];

const DEFAULT_SORT_KEY = SORTABLE_COLUMNS[0]?.key ?? "";
const MAX_VISIBLE_ROWS = 6;
const COLUMN_SCROLL_THRESHOLD = 6;
const TABLE_HEADER_HEIGHT = 56;
const TABLE_ROW_HEIGHT = 56;
const CURSOR_BATCH_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const FALLBACK_LEADS = [
  {
    rowKey: "demo-1",
    name: "Demo Lead",
    email: "demo.lead@example.com",
    status: LeadStatus.NEW_LEADS,
    source: "Website",
    company: "Demo Company",
    phoneNumber: "+919876543210",
    notes: "Requested a demo for next week.",
    businessDetails: "Marketing agency exploring lead automation.",
    followupDate: "18 Mar 2026",
    contactOn: "14 Mar 2026",
  },
];

const compareLeads = (leadA, leadB, sortConfig) => {
  const valueA = leadA?.[sortConfig.key];
  const valueB = leadB?.[sortConfig.key];

  let compareResult = 0;
  if (typeof valueA === "number" && typeof valueB === "number") {
    compareResult = valueA - valueB;
  } else {
    compareResult = String(valueA ?? "").localeCompare(
      String(valueB ?? ""),
      undefined,
      {
        numeric: true,
        sensitivity: "base",
      },
    );
  }

  return sortConfig.direction === "asc" ? compareResult : -compareResult;
};

const LeadsBoard = () => {
  const [sortConfig, setSortConfig] = useState({
    key: DEFAULT_SORT_KEY,
    direction: "asc",
  });
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchValue]);

  const fetchLeadsPage = useCallback(
    async ({ limit, cursor }) => {
      const response = await LeadService.getLeadsCursor({
        limit,
        cursor,
        search: debouncedSearchValue,
      });

      if (!cursor && !debouncedSearchValue && response.data.length === 0) {
        return {
          data: FALLBACK_LEADS,
          nextCursor: null,
        };
      }

      return response;
    },
    [debouncedSearchValue],
  );

  const { rows: visibleLeads, hasMore, isLoading, hasLoadedOnce, containerRef } =
    useLazyCursorPagination(fetchLeadsPage, CURSOR_BATCH_SIZE);

  const leadsToDisplay =
    hasLoadedOnce &&
    !isLoading &&
    !debouncedSearchValue &&
    visibleLeads.length === 0
      ? FALLBACK_LEADS
      : visibleLeads;

  const sortedLeads = useMemo(() => {
    const rows = [...leadsToDisplay];
    rows.sort((leadA, leadB) => compareLeads(leadA, leadB, sortConfig));
    return rows;
  }, [leadsToDisplay, sortConfig]);

  const handleHeaderClick = useCallback((columnKey) => {
    setSortConfig((previousSort) => {
      if (previousSort.key === columnKey) {
        return {
          key: columnKey,
          direction: previousSort.direction === "asc" ? "desc" : "asc",
        };
      }

      return { key: columnKey, direction: "asc" };
    });
  }, []);

  const headerClickHandlers = useMemo(
    () =>
      SORTABLE_COLUMNS.reduce((handlers, column) => {
        handlers[column.key] = () => handleHeaderClick(column.key);
        return handlers;
      }, {}),
    [handleHeaderClick],
  );

  const getStatusColor = useCallback((status) => {
    const normalizedStatus = String(status).trim().toLowerCase();

    if (normalizedStatus === "new lead" || normalizedStatus === "new leads") {
      return LeadStatusColors[LeadStatus.NEW_LEADS];
    }

    const matchedStatus = Object.keys(LeadStatusColors).find(
      (leadStatus) => leadStatus.toLowerCase() === normalizedStatus,
    );

    return matchedStatus ? LeadStatusColors[matchedStatus] : "#e2e8f0";
  }, []);

  const shouldEnableHorizontalScroll =
    SORTABLE_COLUMNS.length > COLUMN_SCROLL_THRESHOLD;
  const tableViewportHeight =
    TABLE_HEADER_HEIGHT + MAX_VISIBLE_ROWS * TABLE_ROW_HEIGHT;
  const shouldShowLoadingState = !hasLoadedOnce && isLoading;
  const shouldShowEmptyState =
    hasLoadedOnce && !isLoading && sortedLeads.length === 0;
  const shouldShowLoadMoreHint =
    hasLoadedOnce && !isLoading && sortedLeads.length > 0 && hasMore;

  return (
    <div className="mb-4 min-w-0">
      <h6 className="fw-bold mb-3">Leads Board</h6>
      <div className="mb-3">
        <input
          type="text"
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          placeholder="Search table..."
          aria-label="Search table"
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition-colors duration-150 placeholder:text-slate-400 focus:border-sky-400"
        />
      </div>
      <div
        ref={containerRef}
        className="w-full min-w-0 max-w-full rounded-2xl"
        style={{
          overflowX: shouldEnableHorizontalScroll ? "auto" : "hidden",
          overflowY: "auto",
          maxHeight: `${tableViewportHeight}px`,
          maxWidth: "100%",
          overscrollBehavior: "contain",
        }}
      >
        <Table
          className="w-max min-w-full"
          searchable={false}
          searchMode="server"
          stickyHeader
          stickyHeaderOffset={0}
        >
          <TableHeader>
            <TableRow>
              {SORTABLE_COLUMNS.map((column) => (
                <TableHead
                  key={column.key}
                  align={column.align ?? "left"}
                  onClick={headerClickHandlers[column.key]}
                  className={cn(
                    "cursor-pointer user-select-none whitespace-nowrap sticky -top-[2px] z-10 bg-slate-100",
                  )}
                  style={{ minWidth: `${column.minWidth}px` }}
                  aria-sort={
                    sortConfig.key === column.key
                      ? sortConfig.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  {column.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {shouldShowLoadingState ? (
              <TableRow>
                <TableCell
                  colSpan={SORTABLE_COLUMNS.length}
                  className="text-center text-slate-500"
                >
                  Loading leads...
                </TableCell>
              </TableRow>
            ) : null}
            {shouldShowEmptyState ? (
              <TableRow>
                <TableCell
                  colSpan={SORTABLE_COLUMNS.length}
                  className="text-center text-slate-500"
                >
                  No leads available.
                </TableCell>
              </TableRow>
            ) : null}
            {sortedLeads.map((lead, rowIndex) => {
              const rowKey = lead.rowKey ?? `row-${rowIndex + 1}`;
              return (
                <TableRow key={rowKey}>
                  {SORTABLE_COLUMNS.map((column) => (
                    <TableCell
                      key={`${column.key}-${rowKey}`}
                      className="whitespace-nowrap break-normal"
                      style={{ minWidth: `${column.minWidth}px` }}
                    >
                      {column.key === "status" ? (
                        <span
                          className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium text-slate-800"
                          style={{
                            backgroundColor: getStatusColor(lead[column.key]),
                          }}
                        >
                          {lead[column.key] ?? "-"}
                        </span>
                      ) : (
                        (lead[column.key] ?? "-")
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {shouldShowLoadMoreHint ? (
              <TableRow>
                <TableCell
                  colSpan={SORTABLE_COLUMNS.length}
                  className="text-center text-slate-400"
                >
                  Scroll to load more leads...
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default LeadsBoard;
